import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatSliderModule } from '@angular/material/slider';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AVAILABLE_YEARS, DAY_NAMES_HU, DayInfo } from '../../../interfaces/daily-report.interface';

@Component({
  selector: 'app-day-selector',
  standalone: true,
  imports: [
    MatSliderModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule
  ],
  templateUrl: './day-selector.component.html',
  styleUrls: ['./day-selector.component.scss']
})
export class DaySelectorComponent implements OnInit, OnDestroy {
  @Input() exportDisabled = false;
  @Input() isExporting = false;

  @Output() dayChanged = new EventEmitter<DayInfo>();
  @Output() exportRequested = new EventEmitter<void>();

  availableYears = AVAILABLE_YEARS;
  selectedYear = AVAILABLE_YEARS[AVAILABLE_YEARS.length - 1];
  currentDay = 1;
  maxDays = 365;

  private readonly sliderInput$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.maxDays = this.getDaysInYear(this.selectedYear);
    this.currentDay = this.getDefaultDayOfYear();

    // 300ms debounce: gyors csúszka-mozgásnál csak az utolsó pozíció indít kérést
    this.sliderInput$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => this.emitDay());
    // Az initial emittálást microtask-ra halasztjuk, hogy elkerüljük az NG0100-t
    Promise.resolve().then(() => this.emitDay());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSliderInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.currentDay = parseInt(target.value, 10);
    this.sliderInput$.next();
  }

  onYearChange(year: number): void {
    this.selectedYear = year;
    this.maxDays = this.getDaysInYear(year);
    this.currentDay = Math.min(this.currentDay, this.maxDays);
    this.emitDay();
  }

  onExportClick(): void {
    this.exportRequested.emit();
  }

  goToPreviousDay(): void {
    this.stepDay(-1);
  }

  goToNextDay(): void {
    this.stepDay(1);
  }

  goToToday(): void {
    const today = new Date();
    if (this.availableYears.includes(today.getFullYear())) {
      this.selectedYear = today.getFullYear();
      this.maxDays = this.getDaysInYear(this.selectedYear);
    }

    this.currentDay = this.getDefaultDayOfYear();
    this.emitDay();
  }

  isToday(): boolean {
    const today = new Date();
    return this.selectedYear === today.getFullYear() && this.currentDay === this.getDefaultDayOfYear();
  }

  /** A csúszka buborékjában a nap sorszáma helyett a dátum jelenjen meg */
  sliderLabel = (value: number): string => {
    return new Date(this.selectedYear, 0, value)
      .toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' });
  };

  getCurrentDayInfo(): DayInfo {
    return this.buildDayInfo(this.currentDay);
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  private emitDay(): void {
    this.dayChanged.emit(this.buildDayInfo(this.currentDay));
  }

  private stepDay(offset: number): void {
    const nextDay = this.currentDay + offset;
    if (nextDay < 1 || nextDay > this.maxDays) {
      return;
    }

    this.currentDay = nextDay;
    this.emitDay();
  }

  private buildDayInfo(dayOfYear: number): DayInfo {
    const date = new Date(this.selectedYear, 0, dayOfYear);
    const weekday = date.getDay();

    return {
      year: this.selectedYear,
      dayOfYear,
      date,
      dayName: DAY_NAMES_HU[weekday],
      isWeekend: weekday === 0 || weekday === 6
    };
  }

  private getDaysInYear(year: number): number {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
  }

  private getDefaultDayOfYear(): number {
    const today = new Date();
    if (today.getFullYear() !== this.selectedYear) {
      return 1;
    }

    const startOfYear = new Date(this.selectedYear, 0, 1);
    const diffDays = Math.floor((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.min(this.maxDays, diffDays + 1));
  }
}
